import { isWeb, useMedia, XStack, YStack } from "tamagui";
import { Button } from "../Button";
import type { ModalFooterProps, ModalSize } from "./types";

/**
 * Returns true if the modal is rendering as a native sheet (small native screens).
 * False means it's a dialog (web or large native screens).
 */
export function useIsNativeSheet() {
  const media = useMedia();
  return !isWeb && (media.sm || media.short);
}

/**
 * Raw token keys for modal padding. Single source of truth — both the
 * $-prefixed strings in modalPaddingBySize for CSS and numeric lookups via
 * spaceTokens for footer size estimation are derived from these keys.
 */
export const modalPaddingTokenKeys = {
  small: {
    horizontal: "sp3",
    footerVertical: "sp3",
    footerButtonGap: "sp1",
    footerLeft: "sp0.5",
    footerRight: "sp3"
  },
  medium: {
    horizontal: "sp4",
    footerVertical: "sp3",
    footerButtonGap: "sp1.5",
    footerLeft: "sp1.5",
    footerRight: "sp4"
  },
  large: {
    horizontal: "sp5",
    footerVertical: "sp4",
    footerButtonGap: "sp1.5",
    footerLeft: "sp2.5",
    footerRight: "sp5"
  }
} as const;
export const modalPaddingBySize = {
  small: {
    horizontal: `$${modalPaddingTokenKeys.small.horizontal}`,
    footerVertical: `$${modalPaddingTokenKeys.small.footerVertical}`,
    footerButtonGap: `$${modalPaddingTokenKeys.small.footerButtonGap}`,
    footerLeft: `$${modalPaddingTokenKeys.small.footerLeft}`,
    footerRight: `$${modalPaddingTokenKeys.small.footerRight}`
  },
  medium: {
    horizontal: `$${modalPaddingTokenKeys.medium.horizontal}`,
    footerVertical: `$${modalPaddingTokenKeys.medium.footerVertical}`,
    footerButtonGap: `$${modalPaddingTokenKeys.medium.footerButtonGap}`,
    footerLeft: `$${modalPaddingTokenKeys.medium.footerLeft}`,
    footerRight: `$${modalPaddingTokenKeys.medium.footerRight}`
  },
  large: {
    horizontal: `$${modalPaddingTokenKeys.large.horizontal}`,
    footerVertical: `$${modalPaddingTokenKeys.large.footerVertical}`,
    footerButtonGap: `$${modalPaddingTokenKeys.large.footerButtonGap}`,
    footerLeft: `$${modalPaddingTokenKeys.large.footerLeft}`,
    footerRight: `$${modalPaddingTokenKeys.large.footerRight}`
  }
} as const;

/**
 * Footer button layout for modals. Shared between DialogModal and native sheet.
 */
export function ModalFooter({
  primaryButtonProps,
  secondaryButtonProps,
  tertiaryButtonProps,
  size,
  isNativeSheet,
  isFading
}: ModalFooterProps & {
  size: ModalSize;
  isNativeSheet: boolean;
}) {
  const media = useMedia();
  const isSmallScreen = media.sm || media.short;
  // Small screens (web or native), small modals, and native sheets always use column layout
  const useColumnLayout = isNativeSheet || size === "small" || isSmallScreen;

  // Use small padding for column layout (matches native sheet behavior)
  const padding = useColumnLayout ? modalPaddingBySize.small : modalPaddingBySize[size];
  const isRowLayout = !useColumnLayout;
  const Container = useColumnLayout ? YStack : XStack;

  // Render buttons in DOM order matching visual order so tab / screen-reader
  // order matches what the user sees. Row paints tertiary…primary L→R (primary
  // last). Column paints primary…tertiary top→bottom (primary first).
  const buttons = [tertiaryButtonProps ? <Button key="tertiary" {...tertiaryButtonProps} variant="tertiary" size="large" {...isRowLayout && {
    fillWidth: false,
    marginRight: "auto"
  }} /> : null, secondaryButtonProps ? <Button key="secondary" {...secondaryButtonProps} variant="secondary" size="large" {...isRowLayout && {
    fillWidth: false
  }} /> : null, primaryButtonProps ? <Button key="primary" {...primaryButtonProps} variant={primaryButtonProps.variant ?? "primary"} size="large" {...isRowLayout && {
    fillWidth: false
  }} /> : null];
  return <Container gap={padding.footerButtonGap} paddingVertical={padding.footerVertical} opacity={isFading ? 0 : 1} transition="300ms ease" {...useColumnLayout ? {
    paddingHorizontal: padding.horizontal
  } : {
    paddingLeft: padding.footerLeft,
    paddingRight: padding.footerRight,
    justifyContent: "flex-end"
  }} data-sentry-element="Container" data-sentry-component="ModalFooter" data-sentry-source-file="ModalParts.tsx">
      {useColumnLayout ? [...buttons].reverse() : buttons}
    </Container>;
}